import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Loader2 } from 'lucide-react';
import { candidatesAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const LABELS = {
  nome: 'Nome', telefone: 'Telefone', data_nascimento: 'Data de nascimento', cidade: 'Cidade', estado: 'Estado',
  resumo: 'Resumo profissional', cargos: 'Cargos de interesse', experiencias: 'Experiências',
  formacoes: 'Formação', cursos: 'Cursos', competencias: 'Competências',
};

const formatDate = (v) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? v.split('-').reverse().join('/') : v);

const describe = (field, value) => {
  if (field === 'data_nascimento') return [formatDate(value)];
  if (field === 'cargos') return value.map((c) => `${c.titulo} (${c.nivel})`);
  if (field === 'experiencias') return value.map((e) => `${e.cargo} · ${e.empresa} · ${e.periodo_inicio}${e.atual ? ' – atual' : e.periodo_fim ? ` – ${e.periodo_fim}` : ''}`);
  if (field === 'formacoes') return value.map((f) => `${f.nivel}: ${f.curso} · ${f.instituicao}${f.ano_conclusao ? ` (${f.ano_conclusao})` : ''}`);
  if (field === 'cursos') return value.map((c) => `${c.nome}${c.instituicao ? ` · ${c.instituicao}` : ''}${c.ano ? ` (${c.ano})` : ''}`);
  if (field === 'competencias') return [value.join(', ')];
  return [String(value)];
};

const FieldRow = ({ field, value, checked, onToggle }) => (
  <label className="flex gap-3 p-3 rounded-lg border border-slate-200 has-[[data-state=checked]]:border-cyan-400 has-[[data-state=checked]]:bg-cyan-50/50 cursor-pointer" data-testid={`cv-autofill-field-${field}`}>
    <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-0.5" data-testid={`cv-autofill-check-${field}`} />
    <div className="min-w-0">
      <p className="text-sm font-semibold text-slate-900">{LABELS[field]}</p>
      {describe(field, value).map((line, i) => (
        <p key={i} className="text-xs text-slate-600 break-words">{line}</p>
      ))}
    </div>
  </label>
);

export const CvAutofillDialog = ({ suggestions, onClose, onApplied }) => {
  const fields = Object.keys(suggestions || {}).filter((f) => LABELS[f]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSelected(Object.keys(suggestions || {})); }, [suggestions]);

  const toggle = (f) => setSelected((s) => (s.includes(f) ? s.filter((x) => x !== f) : [...s, f]));

  const apply = async () => {
    setSaving(true);
    try {
      const res = await candidatesAPI.applyCvAutofill(selected);
      onApplied(res.data.applied);
      toast.success('Dados do currículo adicionados ao seu perfil');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao aplicar os dados');
    } finally { setSaving(false); }
  };

  const dismiss = async () => {
    await candidatesAPI.dismissCvAutofill().catch(() => {});
    onClose();
  };

  return (
    <Dialog open={fields.length > 0} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="w-[calc(100%-2rem)] rounded-xl max-w-md max-h-[85vh] overflow-y-auto" data-testid="cv-autofill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-cyan-600" />Encontramos dados no seu currículo</DialogTitle>
          <DialogDescription>Lemos o currículo inteiro. Mostramos os campos vazios e os itens novos (experiências, cursos, formações e competências) que ainda não estão no seu perfil — nada do que você já tem será alterado.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {fields.map((f) => (
            <FieldRow key={f} field={f} value={suggestions[f]} checked={selected.includes(f)} onToggle={() => toggle(f)} />
          ))}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={dismiss} disabled={saving} data-testid="cv-autofill-dismiss">Agora não</Button>
          <Button onClick={apply} disabled={saving || selected.length === 0} className="bg-cyan-600 hover:bg-cyan-700" data-testid="cv-autofill-apply">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Adicionar selecionados
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
